import produce from 'immer';
import {assertEvent, assign, setup} from 'xstate';

import {assert} from 'Asserts';
import {FullyAccessibleGrid} from 'constants/SampleConstants';
import {PlateMap} from 'samples/plate-maps/PlateMapConstants';
import {
  getDataParameterNamesByVersionId,
  getEmptyPlateMap,
  getInitialDataParameterTags,
  getInitialSelectedDataParameters,
} from 'samples/plate-maps/PlateMapUtil';
import {NO_MATERIAL_INPUT_SENTINEL} from 'samples/plate-maps/transfer-table/FillPlateTransferTableConstants';
import {filterGroupedValidation} from 'samples/plate-maps/validation/PlateMapValidationUtil';
import StorableStore from 'samples/StorableStore';
import {StepItemStatus} from 'ui-library/StepNav';

import {ANNOTATE_FILL_PLATE_MODAL_STEPS, PlateMapType} from '../AnnotateFillPlateModalConstants';
import {
  AnnotateFillPlateModalReducerAction,
  AnnotateFillPlateModalReducerActionType,
  AnnotateFillPlateModalState,
  resetDesignCreateExistingPlateStates,
} from './AnnotateFillPlateMachineConstants';

type AnnotateFillPlateModalContext = AnnotateFillPlateModalState;

type AnnotateFillPlateModalEvents = AnnotateFillPlateModalReducerAction;

/**
 * Finite State Machine for the Annotate Fill Plate Modal
 * Currently this is just acting like a simple reducer as the machine
 * only has one state with no events or transitions.
 *
 * Further migration into a full fledged state machine is coming soon.
 * https://benchling.atlassian.net/wiki/spaces/IR/pages/1241546866/Concentration+Layers+for+Plate+Maps
 *
 * Reach out to @kerry.gould on slack before making changes
 */
export const AnnotateFillPlateModalMachine = setup({
  types: {
    input: {} as AnnotateFillPlateModalContext,
    context: {} as AnnotateFillPlateModalContext,
    events: {} as AnnotateFillPlateModalEvents,
  },
  actions: {
    updatePlateMapType: assign(({context, event}) => {
      assertEvent(event, AnnotateFillPlateModalReducerActionType.UPDATE_PLATE_MAP_TYPE);
      if (context.plateMapType === event.plateMapType) {
        return context;
      }
      return {
        ...resetDesignCreateExistingPlateStates(context),
        plateMapType: event.plateMapType,
      };
    }),
    updateInitialPlateMap: assign(({context, event}) => {
      assertEvent(event, AnnotateFillPlateModalReducerActionType.UPDATE_INITIAL_PLATE_MAP);
      const initialSelectedDataParameters = getInitialSelectedDataParameters(event.plateDataParameters);

      let initialPlateMap: PlateMap | null;
      if (event.updatedPlateMapInfo?.plateMap != null) {
        initialPlateMap = produce(event.updatedPlateMapInfo.plateMap, (draft) => {
          draft.wells.forEach((well) => {
            well.dataParameterTags = getInitialDataParameterTags(initialSelectedDataParameters);
            if (event.isInProcedure) {
              well.contents.forEach((content) => {
                content.materialInput = {materialInputId: NO_MATERIAL_INPUT_SENTINEL, error: null};
              });
            }
          });
          draft.dataParameterNamesByVersionId = getDataParameterNamesByVersionId(event.plateDataParameters);
        });
      } else {
        initialPlateMap = null;
      }
      return {
        initialPlateMap,
        containerSchemaId: event.updatedPlateMapInfo?.containerSchemaId ?? null,
      };
    }),

    updateStepKey: assign(({event, context}) => {
      assertEvent(event, AnnotateFillPlateModalReducerActionType.UPDATE_STEP_KEY);

      const modalSteps = ANNOTATE_FILL_PLATE_MODAL_STEPS;
      const newStepIndex = modalSteps.findIndex((step) => step.key === event.stepKey);
      return produce(context, (draft) => {
        draft.stepKey = event.stepKey;
        modalSteps.forEach((step, index) => {
          draft.statusByStepKey[step.key] =
            index < newStepIndex ? StepItemStatus.DONE : StepItemStatus.UNVISITED;
        });
      });
    }),

    createNewPlateMap: assign(({event, context}) => {
      assertEvent(event, AnnotateFillPlateModalReducerActionType.CREATE_NEW_PLATE_MAP);

      if (context.initialPlateMap == null) {
        const plateSchema =
          context.plateMapType === PlateMapType.DESIGN
            ? context.plateDesignState.plateSchema
            : context.plateMapType === PlateMapType.RECORD
              ? context.createPlatePaneState.selectedSchema
              : null;
        assert(plateSchema != null, 'Expected plate schema to be selected before CREATE_NEW_PLATE_MAP.', {
          plateMapType: context.plateMapType,
        });
        return {
          initialPlateMap: getEmptyPlateMap(
            plateSchema,
            event.containerSchemaFields,
            event.plateDataParameters
          ),
          containerSchemaId: event.containerSchemaId,
        };
      } else {
        return context;
      }
    }),

    updatePlateDefinitionType: assign(({event, context}) => {
      assertEvent(event, AnnotateFillPlateModalReducerActionType.UPDATE_PLATE_DEFINITION_TYPE);
      if (context.plateDefinitionType === event.plateDefinitionType) {
        return context;
      }
      return {
        ...resetDesignCreateExistingPlateStates(context),
        plateDefinitionType: event.plateDefinitionType,
      };
    }),

    updateCreatePlatePaneState: assign(({event, context}) => {
      assertEvent(event, AnnotateFillPlateModalReducerActionType.UPDATE_CREATE_PLATE_PANE_STATE);
      return produce(context, (draft) => {
        draft.createPlatePaneState = event.createPlatePaneState;
        // If a plate map was previously created but the schema changed, clear the plate map.
        if (
          context.initialPlateMap != null &&
          context.createPlatePaneState.selectedSchema?.id !== event.createPlatePaneState.selectedSchema?.id
        ) {
          draft.initialPlateMap = null;
          draft.containerSchemaId = null;
        }
      });
    }),

    updateExistingPlateState: assign(({event, context}) => {
      assertEvent(event, AnnotateFillPlateModalReducerActionType.UPDATE_EXISTING_PLATE_STATE);
      return produce(context, (draft) => {
        draft.existingPlateState = event.existingPlateState;
        if (
          event.existingPlateState.selectedGridId != null &&
          !event.existingPlateState.isValidating &&
          event.existingPlateState.error == null
        ) {
          draft.selectedGrid = StorableStore.getGrid<FullyAccessibleGrid>(
            event.existingPlateState.selectedGridId
          );
        } else if (event.existingPlateState.selectedGridId == null) {
          draft.selectedGrid = null;
        }
      });
    }),

    updatePlateDesignState: assign(({event, context}) => {
      assertEvent(event, AnnotateFillPlateModalReducerActionType.UPDATE_PLATE_DESIGN_STATE);
      return produce(context, (draft) => {
        draft.plateDesignState = event.plateDesignState;
        // If a plate map was previously created but the schema changed, clear the plate map.
        if (
          context.initialPlateMap != null &&
          context.plateDesignState.plateSchema?.id !== event.plateDesignState.plateSchema?.id
        ) {
          draft.initialPlateMap = null;
          draft.containerSchemaId = null;
        }
      });
    }),

    dismissValidationError: assign(({event, context}) => {
      assertEvent(event, AnnotateFillPlateModalReducerActionType.DISMISS_VALIDATION_ERROR);
      return {
        dismissablePlateMapValidation: filterGroupedValidation(
          context.dismissablePlateMapValidation,
          (errorWithUuid) => !event.errorUuids.has(errorWithUuid.uuid)
        ),
      };
    }),
  },
}).createMachine({
  initial: 'idle',
  context: ({input}) => input,
  on: {
    UPDATE_PLATE_MAP_TYPE: {
      actions: 'updatePlateMapType',
    },
    UPDATE_INITIAL_PLATE_MAP: {
      actions: 'updateInitialPlateMap',
    },

    UPDATE_STEP_KEY: {
      actions: 'updateStepKey',
    },

    CREATE_NEW_PLATE_MAP: {
      actions: 'createNewPlateMap',
    },
    UPDATE_PLATE_DEFINITION_TYPE: {
      actions: 'updatePlateDefinitionType',
    },
    UPDATE_CREATE_PLATE_PANE_STATE: {
      actions: 'updateCreatePlatePaneState',
    },
    UPDATE_EXISTING_PLATE_STATE: {
      actions: 'updateExistingPlateState',
    },
    UPDATE_PLATE_DESIGN_STATE: {
      actions: 'updatePlateDesignState',
    },
    DISMISS_VALIDATION_ERROR: {
      actions: 'dismissValidationError',
    },
  },
  states: {
    idle: {},
  },
});
